import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { leadAPI, profileAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import NotificationBell from '../components/NotificationBell';
import { toast } from 'sonner';
import {
  Zap,
  Home,
  User,
  BarChart3,
  Users,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  Search,
  Download,
  Phone,
  Mail,
  CheckCircle,
  Clock,
  Filter,
  Calendar,
  UserPlus,
  ShoppingBag,
  ClipboardList
} from 'lucide-react';

const LeadsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [leads, setLeads] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProfile, setFilterProfile] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leadsRes, profilesRes] = await Promise.all([
          leadAPI.list(),
          profileAPI.list(),
        ]);
        setLeads(leadsRes.data);
        setProfiles(profilesRes.data);
      } catch (error) {
        toast.error('Failed to load leads');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleMarkContacted = async (leadId) => {
    try {
      await leadAPI.update(leadId, { contacted: true });
      setLeads(leads.map(l => l.lead_id === leadId ? { ...l, contacted: true } : l));
      toast.success('Lead marked as contacted');
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const handleExport = async () => {
    try {
      const response = await leadAPI.export();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'leads.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Leads exported!');
    } catch (error) {
      toast.error('Failed to export leads');
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone.includes(searchTerm) ||
      (lead.email && lead.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesProfile = filterProfile === 'all' || lead.profile_id === filterProfile;
    return matchesSearch && matchesProfile;
  });

  const navItems = [
    { icon: <Home className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
    { icon: <User className="w-5 h-5" />, label: 'Profiles', path: '/dashboard' },
    { icon: <CreditCard className="w-5 h-5" />, label: 'Artifacts', path: '/artifacts' },
    { icon: <Calendar className="w-5 h-5" />, label: 'Scheduling', path: '/scheduling' },
    { icon: <UserPlus className="w-5 h-5" />, label: 'Customers', path: '/customers' },
    { icon: <Users className="w-5 h-5" />, label: 'Leads', path: '/leads', active: true },
    { icon: <BarChart3 className="w-5 h-5" />, label: 'Analytics', path: '/analytics' },
    { icon: <ShoppingBag className="w-5 h-5" />, label: 'Shop', path: '/dashboard/shop' },
    { icon: <ClipboardList className="w-5 h-5" />, label: 'Orders', path: '/dashboard/orders' },
    { icon: <Settings className="w-5 h-5" />, label: 'Settings', path: '/settings' },
  ];

  if (user?.role === 'super_admin') {
    navItems.splice(9, 0, { icon: <Zap className="w-5 h-5" />, label: 'Admin', path: '/admin' });
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Signa</span>
          </Link>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item, index) => (
              <li key={index}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                    location.pathname === item.path ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold text-sm">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={handleLogout}>
            <LogOut className="w-5 h-5 mr-3" />
            Sign out
          </Button>
        </div>
      </aside>

      <div className="dashboard-content">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 h-16">
            <button className="lg:hidden p-2 text-slate-600" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Leads</h1>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <Button variant="outline" onClick={handleExport} data-testid="export-leads-btn">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </header>

        <main className="p-6">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search leads..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-leads-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-slate-400" />
              <select
                className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                value={filterProfile}
                onChange={(e) => setFilterProfile(e.target.value)}
                data-testid="filter-profile-select"
              >
                <option value="all">All Profiles</option>
                {profiles.map((profile) => (
                  <option key={profile.profile_id} value={profile.profile_id}>
                    {profile.profile_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="stat-card">
              <p className="text-2xl font-bold text-slate-900">{leads.length}</p>
              <p className="text-sm text-slate-500">Total Leads</p>
            </div>
            <div className="stat-card">
              <p className="text-2xl font-bold text-emerald-600">{leads.filter(l => l.contacted).length}</p>
              <p className="text-sm text-slate-500">Contacted</p>
            </div>
            <div className="stat-card">
              <p className="text-2xl font-bold text-amber-600">{leads.filter(l => !l.contacted).length}</p>
              <p className="text-sm text-slate-500">Pending</p>
            </div>
            <div className="stat-card">
              <p className="text-2xl font-bold text-indigo-600">{leads.filter(l => l.email).length}</p>
              <p className="text-sm text-slate-500">With Email</p>
            </div>
          </div>

          {/* Leads List */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {filteredLeads.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No leads found</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredLeads.map((lead) => {
                  const profile = profiles.find(p => p.profile_id === lead.profile_id);
                  return (
                    <div key={lead.lead_id} className="px-6 py-4 hover:bg-slate-50 transition-colors" data-testid={`lead-row-${lead.lead_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                            <span className="text-indigo-600 font-semibold">{lead.name.charAt(0).toUpperCase()}</span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{lead.name}</p>
                            <div className="flex items-center gap-4 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {lead.phone}
                              </span>
                              {lead.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {lead.email}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {profile && (
                            <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                              {profile.profile_name}
                            </span>
                          )}
                          <span className="text-xs text-slate-400">
                            {new Date(lead.created_at).toLocaleDateString()}
                          </span>
                          {lead.contacted ? (
                            <span className="flex items-center gap-1 text-emerald-600 text-sm">
                              <CheckCircle className="w-4 h-4" />
                              Contacted
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMarkContacted(lead.lead_id)}
                              data-testid={`mark-contacted-${lead.lead_id}`}
                            >
                              <Clock className="w-4 h-4 mr-1" />
                              Mark Contacted
                            </Button>
                          )}
                        </div>
                      </div>
                      {lead.notes && (
                        <p className="mt-2 text-sm text-slate-500 pl-14">{lead.notes}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default LeadsPage;
